import { SignedDistanceField } from '~/engine/core/models/SignedDistanceField';
import { ContourTexture } from './ContourTexture';

// ── Delete-scope planning ────────────────────────────────────────────
//
// Each of the three deletion scopes — this slice, all slices, keep every
// N-th — reduces to "clear this set of texture slices". The committing
// methods on StructureSetObject (`clearContoursOnSlice`,
// `clearAllContours`, `keepEveryNthSlice`) and the Thin tab's preview
// both read the plan from here.
//
// The slice-addressed scope names its slice through
// `planeSliceResolution`, the one derivation the commit uses too, so an
// overlay cannot describe a different set of slices from the one Apply
// clears.
//
// What this does NOT guarantee: that the preview and the commit run
// against the same pixels. The preview is computed on request and Apply
// recomputes from the live texture, so the panel is responsible for
// invalidating its overlay whenever the ROI, the scope, the N value or the
// viewed slice moves — a plan that was right when it was drawn is not
// automatically still right when Apply is pressed.

/**
 * The slices a delete scope will clear. `whole-texture` is kept distinct
 * from "every index" because {@link SignedDistanceField.clearTexture} is
 * the primitive `clearAllContours` actually calls; expressing it as a
 * slice list would preview a different call from the one that commits.
 */
export type DeleteScopePlan =
    | { readonly kind: 'whole-texture' }
    | { readonly kind: 'slices'; readonly slices: readonly number[] };

/**
 * Rejects a keep-every-N-th value that would not describe a cadence.
 * Separate from {@link planKeepEveryNthSliceClears} so the committing path
 * can validate BEFORE it opens a modify session, and so the preview can
 * refuse the same values without opening one at all.
 */
export function validateKeepEveryNthSliceValue(
    keepEveryNthSliceValue: number
): void {
    // `slice % N` yields NaN for N=0/NaN, which would silently clear
    // every slice except first/last.
    if (
        !Number.isInteger(keepEveryNthSliceValue) ||
        keepEveryNthSliceValue < 2
    ) {
        throw new Error(
            `keepEveryNthSliceValue must be an integer >= 2 ` +
                `(got: ${keepEveryNthSliceValue})`
        );
    }
}

/**
 * The slices keep-every-N-th clears.
 *
 * The first and last slices holding contour content are preserved
 * unconditionally, whether or not they fall on the cadence, so the
 * structure's Z extent is never shortened. A plain `z % n !== 0` rule
 * would drop them and silently shorten the structure — the preview must
 * show the same forced keeps the commit performs, so both take this list.
 *
 * @returns the slice indices to clear, or `undefined` when the ROI holds
 * no content at all (there is nothing to thin, and no first/last slice to
 * anchor the cadence to).
 */
export function planKeepEveryNthSliceClears(
    sdf: SignedDistanceField,
    keepEveryNthSliceValue: number
): number[] | undefined {
    validateKeepEveryNthSliceValue(keepEveryNthSliceValue);

    // Identify the first and last slices that hold contour content, in
    // voxel/slice-index space.
    const contentBounds = sdf.getContentBounds(ContourTexture.SDF_THRESHOLD);
    if (!contentBounds) {
        return undefined;
    }
    const firstContentSlice = contentBounds.min[2];
    const lastContentSlice = contentBounds.max[2];

    return sdf
        .getAllSlices()
        .filter(
            (slice) =>
                slice !== firstContentSlice &&
                slice !== lastContentSlice &&
                slice % keepEveryNthSliceValue !== 0
        );
}

/**
 * A copy of `source` with the plan applied, for the preview overlay to
 * diff against the live ROI. The caller owns the returned texture and must
 * delete it.
 *
 * It clears through the SAME `clearTexture` / `clearSlice` primitives the
 * committing methods use, rather than reproducing their effect with a
 * different write (a bounds crop, a boolean subtraction), so no argument
 * about equivalence is needed: the preview runs the operation.
 */
export function createDeletedPreviewTexture(
    source: ContourTexture,
    plan: DeleteScopePlan
): ContourTexture {
    const preview = source.clone();
    try {
        const sdf = preview.signedDistanceField;
        if (plan.kind === 'whole-texture') {
            sdf.clearTexture();
        } else {
            plan.slices.forEach((sliceZ) => sdf.clearSlice(sliceZ));
        }
    } catch (e) {
        // Release with acquisition: the clone is already a live GPU
        // texture and nothing downstream will hear about it if we throw.
        preview.delete();
        throw e;
    }
    return preview;
}

/**
 * How many of the planned slices actually hold contour content — i.e.
 * whether the plan changes anything at all. Drives the panel's "this would
 * not delete anything" line, so an empty diff overlay reads as "nothing to
 * remove" rather than as a preview that failed to draw.
 *
 * Advisory only, and deliberately not a gate on Apply: it says whether a
 * slice holds content, not how much of that content the diff will show.
 * The `>= threshold` comparison in
 * {@link SignedDistanceField.isSliceEmpty} and the `> threshold` used for
 * the content bounds agree on uint8 pixel data, because no byte value
 * equals the half-integer iso threshold.
 */
export function countClearedSlicesWithContent(
    sdf: SignedDistanceField,
    plan: DeleteScopePlan
): number {
    const pixels = sdf.pixels;
    const sliceIndices =
        plan.kind === 'whole-texture' ? sdf.getAllSlices() : plan.slices;
    return sliceIndices.filter(
        (sliceZ) =>
            pixels[sliceZ] !== undefined &&
            !SignedDistanceField.isSliceEmpty(
                pixels[sliceZ],
                ContourTexture.SDF_THRESHOLD
            )
    ).length;
}
